import { z } from 'zod';
import { DeviceIdentity } from './events.js';
/**
 * What BoardFlight's own clients report about their use (`docs/off-board-plan.md` §5.6; the Board
 * client's first design of it is `docs/archive/onboarding-plan.md`).
 *
 * The Board client that wrote the first rows is gone. The storefront writes them now, in two
 * shells — a browser tab and the desktop app's window — and says which in `surface`.
 *
 * Not game analytics — that is `analytics.ts`, about what happens *inside* a game a team shipped.
 * This is BoardFlight's product funnel: what a household did with the app itself on the way to its
 * first game. Read by the admin console and by nothing else; no team ever sees a row of it.
 *
 * ## The vocabulary is closed, and the wire is not
 *
 * `CLIENT_EVENT_NAMES` is the list the server keeps. The request accepts any name of the right
 * *shape* and the route drops the ones not on the list, rather than refusing the batch: a page
 * from a later deploy sends an event this Worker has never heard of in the same batch as five it
 * has, and a 400 would lose all six. Junk costs a dropped row, never a stored one.
 *
 * **This file is the list.** The one writer left is the storefront's reporter
 * (`apps/web/src/storefront/welcome/events.ts`) — the onboarding, and Play in the desktop app's
 * window — which takes its names and the shape of every event's properties from here
 * ({@link ClientEventProps}), so a name cannot be reported without a line in this file. The Board
 * client's own copy of the list went with the client.
 */
export const CLIENT_EVENT_NAMES = [
    /**
     * The onboarding opened: on a Board that had never been through it, in a desktop app that had
     * not finished it, or in a browser. `props.entry` (off the Board only) is `auto` when it opened
     * by itself, `invite` when somebody pressed the card that offers it, and `direct` when they went
     * to its address.
     */
    'onboarding_started',
    /** A step came on screen. `props.step` is one of `ONBOARDING_STEPS`. */
    'onboarding_step_viewed',
    /** The piece step's answer. `props.sets` is what was ticked; `props.defaults_kept` whether it was untouched. */
    'onboarding_pieces_confirmed',
    /**
     * Pair this Board was pressed, so Board put its own approval prompt up. `props.attempt` counts
     * them; `props.method` (off the Board only) is `approval`, or `code` for the six-digit fallback.
     */
    'onboarding_pair_requested',
    /** Board approved the pairing — wherever the household had got to by then. */
    'onboarding_pair_linked',
    /** Board refused, did not answer, or could not be reached. `props.reason`: `refused`, `timeout`, `unreachable`, `error`. */
    'onboarding_pair_failed',
    /** Skip on the pair step. `props.requested` says whether Board's prompt had been asked for first. */
    'onboarding_pair_skipped',
    /** A sign-in code reached the screen, so a phone could have been used. */
    'onboarding_account_code_shown',
    /** A session arrived while the account step was up. Sent authenticated, so it carries the account. */
    'onboarding_account_linked',
    /** The code was declined, expired, or could not be fetched. `props.reason` says which. */
    'onboarding_account_failed',
    /** Skip on the account step. */
    'onboarding_account_skipped',
    /** The last step's button: the household is in the library. */
    'onboarding_completed',
    /**
     * The household left the onboarding without finishing it. `props.step` is where; `props.via` is
     * how. On the Board that was leaving BoardFlight itself — `back` from the first step, or `menu`
     * for Exit in the corner. Off the Board it is `library`, the way out every step offers, which
     * also says "do not open this by itself again".
     */
    'onboarding_exited',
    // Off the Board (`docs/off-board-plan.md` §5.6). Every name above keeps its meaning on the
    // surfaces that still have the step; these are the steps the Board client never had.
    /** The download for the reader's system was pressed on the web's *Get the app* step. `props.os`: `macos`, `windows`, `linux`. */
    'onboarding_app_download_clicked',
    /** The desktop app opened its onboarding for the first time on this computer. */
    'onboarding_app_opened',
    /**
     * *Find your Board* found one. `props.transport` is how — `mdns`, `sweep`, `remembered`, `typed` —
     * and `props.seconds` how long the search had run.
     */
    'onboarding_board_found',
    /** A search ended with nothing. `props.tried` lists the transports it looked with. */
    'onboarding_board_not_found',
    /** "Keep BoardFlight running" was answered. `props.enabled` is the answer. */
    'onboarding_background_chosen',
    /**
     * The web's *Waiting* step turned over: a Board appeared on the account, connected by a desktop
     * app. `props.seconds_waiting` is how long the page had watched.
     */
    'onboarding_handoff_linked',
    // Past the onboarding.
    /**
     * Play was pressed in the desktop app's window and the Board said yes to the launch
     * (`launchOn`, `storefront/boards/OnBoard.tsx`). `props.app_id` and `props.board_id` say which
     * game and which of the account's Boards. **An account's record, never a play**: it says what
     * the account asked for, and the admin console lists it in that account's activity
     * (`docs/admin-plan.md` §3.4). Nothing joins it to a play session, a game event or an install
     * id, by id or by time, and nothing counts it as a play — the game reports its own start
     * (`docs/off-board-plan.md` §6). Kept only from a signed-in request
     * ({@link ACCOUNT_CLIENT_EVENTS}).
     */
    'game_launched',
];
export function isClientEventName(name) {
    return CLIENT_EVENT_NAMES.includes(name);
}
/**
 * The names the route keeps only when the request carried a session, and drops otherwise.
 *
 * A launch is pressed by a signed-in account on its own Board, so one arriving with nobody signed
 * in is a session that ended between the press and the send. Kept anonymous, it would still carry
 * a Board's id, and that names the account by a join — so it is not kept at all.
 */
export const ACCOUNT_CLIENT_EVENTS = ['game_launched'];
/**
 * Every onboarding step any surface has — the values `onboarding_step_viewed` carries in
 * `props.step`.
 *
 * **A vocabulary, not an order.** The first five are the Board client's, in the order it showed
 * them, and nothing about them changed: the console's funnel still counts `pieces`, `pair`,
 * `account` and `done` by name. The rest are the steps that exist only off the Board. Which steps a
 * surface shows, and in what order, is {@link ONBOARDING_SURFACE_STEPS}.
 */
export const ONBOARDING_STEPS = [
    'welcome',
    'pieces',
    'pair',
    'account',
    'done',
    /** Web: the download for this system, and "I already have it". */
    'get_app',
    /** Web: the page watches the account and turns over when a Board appears. */
    'waiting',
    /** Desktop: mDNS, the sweep, a typed address. */
    'find',
    /** Desktop: "Keep BoardFlight running". */
    'background',
    /**
     * The phone's app: notifications from BoardFlight, asked once, before the system's own question
     * (`docs/phone-app-plan.md` §10). A phone is the desktop surface, so it is in that list and is
     * skipped wherever the app cannot be sent one, the way `background` is skipped on a phone.
     */
    'notifications',
];
/**
 * Where a report came from: a browser tab, or the desktop app's window.
 *
 * Absent on a row is the retired Board client — every row written before this field existed.
 */
export const CLIENT_SURFACES = ['web', 'desktop'];
export const ClientSurface = z.enum(CLIENT_SURFACES);
/**
 * The onboarding each surface walks, in order (`docs/off-board-plan.md` §5.6).
 *
 * Two lists rather than one longer one: the surfaces share step components and do not share a
 * path. The desktop app asks for the account first because finding a Board is pointless without
 * one, and asks about pieces only once a Board exists — as the website does. A funnel is read
 * against its own surface's list; laying one surface's rows over the other's order reads a step
 * the surface never had as a drop to zero.
 */
export const ONBOARDING_SURFACE_STEPS = {
    desktop: ['welcome', 'account', 'find', 'pair', 'pieces', 'notifications', 'background', 'done'],
    web: ['welcome', 'account', 'get_app', 'waiting', 'pieces', 'done'],
};
/**
 * The three groups a read sorts rows into: the two surfaces, and `board` for every row that names
 * none — what the retired Board client wrote before the field existed. A read shows that group as
 * its own, under its own step order ({@link ONBOARDING_BOARD_STEPS}); it never folds those rows
 * into a surface and never drops them.
 */
export const CLIENT_EVENT_SOURCES = ['desktop', 'web', 'board'];
/** The onboarding the Board client walked, in the order it showed it. Read against its rows only. */
export const ONBOARDING_BOARD_STEPS = ['welcome', 'pieces', 'pair', 'account', 'done'];
/** The path a group's rows are read against. */
export function onboardingStepsOf(source) {
    return source === 'board' ? ONBOARDING_BOARD_STEPS : ONBOARDING_SURFACE_STEPS[source];
}
/**
 * The sets a regular Board comes with, which the piece step starts ticked.
 *
 * Here so the piece step and the console agree on them: the console says how many households
 * answered with exactly these — the question "does anybody change the default?".
 */
export const ONBOARDING_DEFAULT_PIECES = ['arcade', 'chop_chop'];
/** How long a row is kept. Long enough to compare a funnel with the same month a year before. */
export const CLIENT_EVENT_RETENTION_DAYS = 400;
/** One request carries at most this many events. The client batches below it. */
export const MAX_CLIENT_EVENTS_PER_REQUEST = 25;
/**
 * The most an event's `props` may take up once stored, as JSON. The schema alone allows an event
 * to carry tens of kilobytes, and the endpoint is anonymous; nothing the client reports needs more
 * than a few hundred bytes. An event over this is dropped like an unknown name, not refused.
 */
export const MAX_CLIENT_EVENT_PROPS_BYTES = 2048;
/**
 * A property value: flat, small, and nothing that could carry a document.
 *
 * A list of short strings is allowed because the piece answer is one, and it is the most useful
 * thing the onboarding reports. Nothing nests: a funnel is counted, not queried.
 */
const PropValue = z.union([
    z.string().max(200),
    z.number().finite(),
    z.boolean(),
    z.array(z.string().max(64)).max(32),
]);
export const ClientEvent = z.object({
    /**
     * Minted by the client, so a batch resent after a lost response is kept once. Unique per
     * install, not globally — the server keys on `(installId, id)`.
     */
    id: z.string().regex(/^[A-Za-z0-9-]{8,64}$/),
    /** Any well-formed name; the route keeps only `CLIENT_EVENT_NAMES`. See the header. */
    name: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
    /** The reporter's own clock, epoch ms. Context only — it is not trusted for ordering. */
    at: z.number().int().nonnegative(),
    props: z
        .record(z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), PropValue)
        .refine((value) => Object.keys(value).length <= 16, 'At most 16 properties.')
        .default({}),
});
export const ClientEventsRequest = z.object({
    /**
     * A random id the app minted the first time it ran, kept until its data is cleared.
     *
     * The funnel's unit. Not the Board's identity — a reinstall is a new install, which is the
     * honest reading of a household starting over — and not an account, because most of what this
     * reports happens before there is one.
     */
    installId: z.string().regex(/^[A-Za-z0-9-]{8,64}$/),
    /**
     * Which release said it, so a funnel can be split by one: the desktop app's version (`1.4.0`),
     * the website's build id, or — from the Board client — `0.1.42 (42)`.
     */
    clientVersion: z.string().trim().max(40).nullish(),
    /**
     * Which shell said it. One install is one surface, so it is said once for the batch and the
     * server stamps it on every row. `installId` is per browser profile on `web` and per desktop
     * install on `desktop`. Absent from the retired Board client, and stored as absent.
     */
    surface: ClientSurface.nullish(),
    device: DeviceIdentity.nullish(),
    events: z.array(ClientEvent).min(1).max(MAX_CLIENT_EVENTS_PER_REQUEST),
});
//# sourceMappingURL=client-events.js.map